import React from "react";
import { useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { SITE } from "@/data/config";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";
const CONTENT = {
  privacy: {
    title: "Privacy",
    intro: "What Kostaja collects and how it is used.",
    metaDescription: "Kostaja privacy policy. What we collect from forms, how it is used to reply to your inquiry, cookie policy, and how to have your data removed.",
    sections: [
      { h: "What we collect", p: "When you submit a form on this site, we receive the details you choose to provide such as your name, email, phone, business, website, message, and consent flag. We do not collect sensitive payment information directly. Payments use Stripe Payment Links or Shopify checkout, both of which manage card data on their own platforms." },
      { h: "How we use it", p: "We use submissions only to respond to your inquiry, scope a project, and follow up. We do not sell or share your information." },
      { h: "Cookies", p: "This site uses minimal analytics to understand which pages help visitors and which fall short. You can block cookies in your browser without losing site functionality." },
      { h: "Your choices", p: "You can ask us to remove your inquiry from our records at any time by emailing " + SITE.email + "." },
    ],
  },
  terms: {
    title: "Terms",
    intro: "Plain-English working terms.",
    metaDescription: "Kostaja working terms. Written scope, payments, revision rounds, ownership transfer on payment, and outcome limitations for every engagement.",
    sections: [
      { h: "Scope and deliverables", p: "All work is governed by a written scope letter at project start. The scope letter names the outcome, what you will receive, what is not included, what we need from you, the timeline, and the price." },
      { h: "Payments", p: "Smaller fixed-scope services use Stripe Payment Links where available. Larger projects use a deposit, then a balance at delivery. Deposits are never sold as standalone services. Shop resources are purchased on Shopify." },
      { h: "Revisions", p: "Each service includes the revision rounds named in its detail page. Additional rounds are quoted in plain English before any work begins." },
      { h: "Ownership", p: "Final approved deliverables transfer to you on full payment. Working files and intermediate iterations remain with Kostaja unless otherwise written into the scope." },
      { h: "Limitations", p: "Kostaja does not guarantee specific business outcomes, rankings, traffic, or sales numbers. We do guarantee the quality of the work delivered against the written scope." },
    ],
  },
  "platform-notes": {
    title: "Platform Notes",
    intro: "Third-party platform names are used descriptively only.",
    metaDescription: "Kostaja is an independent studio. Third-party platform names on this site are used descriptively only and do not imply affiliation, endorsement, or sponsorship.",
    sections: [
      { h: "Independent studio", p: "Kostaja is an independent studio. We are not affiliated with, endorsed by, or sponsored by Canva, Adobe, GoDaddy Studio, Google, Shopify, Stripe, Spotify, Calendly, Meta, or LinkedIn unless explicitly stated." },
      { h: "Trademarks", p: "All trademarks belong to their respective owners." },
    ],
  },
};

const Legal = () => {
  const { slug } = useParams();
  const key = slug || "privacy";
  const data = CONTENT[key] || CONTENT.privacy;
  useSeo({
    title: `${data.title} | Kostaja`,
    description: data.metaDescription || data.intro,
    canonical: `${SITE.url}/legal/${key}`,
  });
  return (
    <main data-testid={`legal-page-${key}`} className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { to: "/legal/privacy", label: "Legal" }, { label: data.title }]} />
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">{data.title}</h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">{data.intro}</p>
        </div>
      </section>
      <section className="bg-black border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-14 lg:py-20 space-y-10">
          {data.sections.map((s, idx) => (
            <div key={s.h}>
              <Overline className="mb-2">Section 0{idx + 1}</Overline>
              <h2 className="text-2xl lg:text-3xl font-black tracking-tighter mb-3">{s.h}</h2>
              <p className="text-white/80 leading-relaxed">{s.p}</p>
            </div>
          ))}
          <div className="pt-6 border-t border-white/10 flex flex-wrap gap-3">
            <SecondaryButton to="/legal/privacy">Privacy</SecondaryButton>
            <SecondaryButton to="/legal/terms">Terms</SecondaryButton>
            <SecondaryButton to="/legal/platform-notes">Platform Notes</SecondaryButton>
            <PrimaryButton to="/contact">Questions</PrimaryButton>
          </div>
        </div>
      </section>
      <CTABlock variant="default" />
    </main>
  );
};

export default Legal;
